import React, {
  ComponentProps,
  FunctionComponent,
  useEffect,
  useState
} from 'react'
import { PureShareButtons } from '@ta-interaktiv/react-share-buttons'
import { ScrollTracking } from './scrollTracking'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import styles from './masthead.module.scss'

interface Props extends ComponentProps<typeof PureShareButtons> {
  /** The outwards facing media name. */
  fullMediaName?: string

  /**  The home page of the medium to link to. */
  homepage?: string

  /** Whether the masthead should appear bright on a dark background. */
  inverted?: boolean

  /** The media domain name whose logo should be shown. */
  mediaName?: string

  children: React.ReactNode
}

/**
 * A masthead component that shows the publication logo and share buttons.
 * This version needs to be fully configured. You may want to consider using
 * the polymorphic version instead.
 *
 * ## Installation
 *
 * ```bash
 * yarn add @ta-interaktiv/react-masthead
 * ```
 *
 * ## Usage
 * ```jsx
 * import React from 'react'
 * import { PureMasthead as Masthead } from '@ta-interaktiv/react-masthead'
 *
 * export const Component() {
 *   return (
 *     <Masthead
 *        mediaName='dasmagazin'
 *        homepage='https://www.dasmagazin.ch'
 *        fullMediaName='Das Magazin'
 *     />
 *   )
 *
 * }
 * ```
 *
 */
export const Masthead: FunctionComponent<Props> = props => {
  // Set up defaults
  const {
    mediaName = 'tagesanzeiger',
    inverted = false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    clickHandler = function() {},
    homepage = '//www.tagesanzeiger.ch',
    fullMediaName = 'Tages-Anzeiger',
    url
  } = props

  // Prepare constants for the inverted version
  const invertedClass = inverted ? 'inverted' : ''
  const invertedImgSrcName = inverted ? '-inverted' : ''

  const [isInApp, setIsInApp] = useState(false)
  useEffect(() => {
    setIsInApp(new URL(window.location.href).searchParams.has('appwebview'))
  }, [])

  return (
    <div
      className={`ta-masthead masthead-component ${invertedClass} ${styles.mastheadComponent}`}
    >
      <div className='ui container '>
        <div className={styles.grid}>
          <a href={homepage} className={styles.logoContainer}>
            <img
              src={`//interaktiv.tagesanzeiger.ch/static/icons/${mediaName}${invertedImgSrcName}.svg`}
              alt={fullMediaName}
              className={styles.iconOnly}
            />
            <img
              src={`//interaktiv.tagesanzeiger.ch/static/logos/${mediaName}${invertedImgSrcName}.svg`}
              alt={fullMediaName}
              className={styles.fullLogo}
            />
          </a>

          <div className={styles.middleContent}>{props.children}</div>

          <div className={styles.shareButtons}>
            {!isInApp && (
              <PureShareButtons
                {...props}
                url={url}
                clickHandler={clickHandler}
              />
            )}
          </div>
        </div>
      </div>
      <ScrollTracking />
    </div>
  )
}
